// ====================================
// File: templates/CreativeResumeTemplate.jsx
// ====================================
import React from "react";

const CreativeResumeTemplate = ({ data }) => {
  if (!data) return null;
  const { header, mainContent, sidebarContent } = data;

  // Helper function to render bullet points
  const renderBulletPoints = (text) => {
    if (!text) return null;

    // Handle both string and HTML content
    const textContent = typeof text === "string" ? text : "";

    return textContent
      .split("\n")
      .map((line, idx) => {
        const cleanLine = line.replace(/^[•·▪▸-]\s*/, "").trim();
        if (!cleanLine) return null;

        return (
          <li key={idx} className="creative-bullet-item">
            {cleanLine}
          </li>
        );
      })
      .filter(Boolean);
  };

  // Helper to extract text from HTML
  const extractText = (html) => {
    if (!html) return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  // Helper function for contact icons
  const getContactIcon = (type) => {
    switch (type) {
      case "phone":
        return "📞";
      case "email":
        return "✉️";
      case "location":
        return "📍";
      case "linkedin":
        return "💼";
      case "website":
        return "🌐";
      default:
        return "•";
    }
  };

  return (
    <div className="creative-resume">
      {/* Left Sidebar with dark background */}
      <div className="creative-sidebar">
        <div className="creative-header">
          <h1>{header?.name || "YOUR NAME"}</h1>
          <p className="creative-title">
            {header?.subtitle || "Professional Title"}
          </p>
        </div>

        <div className="creative-contact">
          {header?.contactInfo?.map((contact, idx) => (
            <div key={idx} className="creative-contact-item">
              <span className="contact-icon">
                {getContactIcon(contact.type)}
              </span>
              <span>{contact.value}</span>
            </div>
          ))}
        </div>

        {/* Sidebar sections */}
        {sidebarContent?.map((section) => (
          <div key={section.id} className="creative-sidebar-section">
            <h3>{section.title}</h3>

            {section.type === "education" && (
              <div className="creative-education">
                {section.items?.map((item) => (
                  <div
                    key={item.id || Math.random()}
                    className="creative-edu-item"
                  >
                    <h4>{item.degree || "Degree"}</h4>
                    <p>{item.institution || "Institution"}</p>
                    <p className="creative-period">{item.period || ""}</p>
                    {item.details && (
                      <ul className="creative-edu-details">
                        {renderBulletPoints(item.details)}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}

            {["competencies", "technical"].includes(section.type) && (
              <ul className="creative-skills-list">
                {section.items?.map((item, idx) => (
                  <li key={idx}>
                    {typeof item === "string" ? item : item.name || item}
                  </li>
                ))}
              </ul>
            )}

            {section.type === "languages" && (
              <ul className="creative-skills-list">
                {section.items?.map((item, idx) => (
                  <li key={idx}>
                    {typeof item === "string"
                      ? item
                      : item.name
                      ? `${item.name}${
                          item.proficiency ? ` (${item.proficiency})` : ""
                        }`
                      : ""}
                  </li>
                ))}
              </ul>
            )}

            {section.type === "certifications" && (
              <ul className="creative-skills-list">
                {section.items?.map((item, idx) => (
                  <li key={item.id || idx}>
                    {item.name || item}
                    {item.period && ` - ${item.period}`}
                  </li>
                ))}
              </ul>
            )}

            {section.type === "interests" && (
              <div className="creative-interests">
                {section.items?.map((item, idx) => (
                  <span key={idx} className="creative-interest-tag">
                    {typeof item === "string" ? item : item.name || item}
                  </span>
                ))}
              </div>
            )}

            {section.type === "awards" && (
              <div className="creative-awards">
                {section.items?.map((item, idx) => (
                  <div key={item.id || idx} className="creative-award-item">
                    <h4>{item.name || "Award"}</h4>
                    {item.year && (
                      <p className="creative-award-year">{item.year}</p>
                    )}
                    {item.description && <p>{item.description}</p>}
                  </div>
                ))}
              </div>
            )}

            {section.type === "publications" && (
              <div className="creative-publications">
                {section.items?.map((item, idx) => (
                  <div key={item.id || idx} className="creative-pub-item">
                    <h4>{item.title || "Publication"}</h4>
                    <p className="creative-pub-info">
                      {item.publisher || ""}
                      {item.year && `, ${item.year}`}
                    </p>
                    {item.description && <p>{item.description}</p>}
                  </div>
                ))}
              </div>
            )}

            {section.type === "social" && (
              <div className="creative-social">
                {section.items?.map((item, idx) => (
                  <div key={item.id || idx} className="creative-social-item">
                    <h4>{item.title || "Social Impact"}</h4>
                    {item.organization && <p>{item.organization}</p>}
                    {item.description && (
                      <ul className="creative-social-desc">
                        {renderBulletPoints(item.description)}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Right Main Content Area */}
      <div className="creative-main">
        {header?.tagline && (
          <div className="creative-tagline">{extractText(header.tagline)}</div>
        )}

        {/* Main content sections */}
        {mainContent?.map((section) => (
          <div key={section.id} className="creative-main-section">
            <h2>{section.title}</h2>

            {section.type === "profile" && (
              <div className="creative-profile">
                {extractText(section.content)}
              </div>
            )}

            {section.type === "experience" && (
              <div className="creative-experiences">
                {section.items?.map((item, idx) => (
                  <div key={item.id || idx} className="creative-job">
                    <h3>{item.jobTitle || "Position"}</h3>
                    <p className="creative-company">
                      {item.companyInfo || "Company"}
                    </p>
                    <ul className="creative-job-details">
                      {renderBulletPoints(item.details)}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {section.type === "volunteer" && (
              <div className="creative-experiences">
                {section.items?.map((item, idx) => (
                  <div key={item.id || idx} className="creative-job">
                    <h3>{item.role || "Volunteer Role"}</h3>
                    <p className="creative-company">
                      {item.organization || "Organization"}
                    </p>
                    <ul className="creative-job-details">
                      {renderBulletPoints(item.details)}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {section.type === "projects" && (
              <div className="creative-experiences">
                {section.items?.map((item, idx) => (
                  <div key={item.id || idx} className="creative-job">
                    <h3>{item.title || "Project"}</h3>
                    <p className="creative-company">
                      {item.technologies || "Technologies"}
                    </p>
                    <ul className="creative-job-details">
                      {renderBulletPoints(item.description)}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {section.type === "achievements" && (
              <ul className="creative-achievements">
                {section.items?.map((item, idx) => (
                  <li key={item.id || idx}>
                    <strong>{item.title || "Achievement"}</strong>
                    {item.description && `: ${item.description}`}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}

        {/* If no main content, show placeholder */}
        {(!mainContent || mainContent.length === 0) && (
          <div className="creative-main-section">
            <p style={{ color: "#7f8c8d", fontStyle: "italic" }}>
              Add experience, projects, or other main content sections to
              complete your resume.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreativeResumeTemplate;
